import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const NAV_ITEMS = [
  { id: 'getting-started', label: 'Getting Started', num: '01' },
  { id: 'authentication', label: 'Authentication', num: '02' },
  { id: 'json-feed', label: 'JSON Feed', num: '03' },
  { id: 'single-article', label: 'Single Article', num: '04' },
  { id: 'rss-feed', label: 'RSS Feed', num: '05' },
  { id: 'embed-widget', label: 'Embed Widget', num: '06' },
  { id: 'languages', label: 'Languages', num: '07' },
];

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observers = [];
    const visible = new Map();
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          visible.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
          let best = null; let bestRatio = -1;
          for (const [k, v] of visible.entries()) { if (v > bestRatio) { bestRatio = v; best = k; } }
          if (best) setActive(best);
        },
        { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [ids.join(',')]);
  return active;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text.trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className={`text-[9px] font-black uppercase tracking-widest transition-all px-2 py-1 rounded ${
        copied ? 'text-[#CCFF00]' : 'text-slate-600 hover:text-slate-300'
      }`}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

function CodeBlock({ children, lang }) {
  const text = typeof children === 'string' ? children.trim() : '';
  return (
    <div className="relative my-5 rounded-xl overflow-hidden border border-white/[0.08] bg-[#080808] shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-white/[0.025]">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]/70" />
          </div>
          <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-600">{lang}</span>
        </div>
        <CopyButton text={text} />
      </div>
      <pre className="px-5 py-5 overflow-x-auto text-[13px] font-mono text-slate-300 leading-relaxed">
        <code>{text}</code>
      </pre>
    </div>
  );
}

function Section({ id, num, children }) {
  return (
    <section id={id} className="scroll-mt-28 pb-16 mb-4 border-b border-white/[0.05] last:border-0">
      {children}
    </section>
  );
}

function SectionTitle({ children, num }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      {num && (
        <span className="font-mono text-[10px] font-black text-[#CCFF00]/40 select-none">{num}</span>
      )}
      <h2 className="text-2xl font-black tracking-tight text-white">{children}</h2>
    </div>
  );
}

function Para({ children, className = '' }) {
  return <p className={`text-slate-400 leading-relaxed mb-4 ${className}`}>{children}</p>;
}

function Mono({ children }) {
  return (
    <code className="font-mono text-[12px] bg-[#CCFF00]/[0.07] border border-[#CCFF00]/[0.12] rounded px-1.5 py-0.5 text-[#CCFF00]/90">
      {children}
    </code>
  );
}

function EndpointBar({ method, path }) {
  return (
    <div className="flex items-center gap-3 my-5 rounded-xl border border-[#CCFF00]/[0.12] bg-[#CCFF00]/[0.03] px-4 py-3 shadow-[0_0_24px_rgba(204,255,0,0.04)]">
      <span className="inline-flex items-center rounded border border-[#CCFF00]/40 bg-[#CCFF00]/[0.1] px-3 py-1 font-mono text-[11px] font-black text-[#CCFF00] tracking-widest">
        {method}
      </span>
      <code className="font-mono text-sm text-slate-100">{path}</code>
    </div>
  );
}

function SubHeading({ children }) {
  return (
    <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-slate-500 mt-8 mb-3">
      <span className="w-3 h-px bg-slate-700" />
      {children}
    </h3>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto my-5 rounded-xl border border-white/[0.07] bg-[#080808]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-white/[0.07]">
            {headers.map((h) => (
              <th key={h} className="text-left px-5 py-3.5 text-[10px] font-black uppercase tracking-[0.22em] text-slate-600 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b border-white/[0.04] last:border-0 transition-colors hover:bg-[#CCFF00]/[0.02] ${i % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
              {row.map((cell, j) => (
                <td key={j} className="px-5 py-3.5 align-top text-[13px] text-slate-300 leading-relaxed">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ children, type = 'warning' }) {
  const styles = {
    warning: 'border-amber-500/20 bg-amber-500/[0.04] text-amber-200/80',
    info: 'border-[#CCFF00]/15 bg-[#CCFF00]/[0.03] text-[#CCFF00]/80',
  };
  const icons = { warning: '⚠', info: 'ℹ' };
  return (
    <div className={`my-5 rounded-xl border px-5 py-4 text-sm leading-relaxed flex gap-3 ${styles[type]}`}>
      <span className="text-base shrink-0 mt-0.5">{icons[type]}</span>
      <span>{children}</span>
    </div>
  );
}

const QUICK_START = [
  {
    label: 'JSON API',
    desc: 'For developers building a custom integration.',
    href: '#json-feed',
    icon: (
      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
      </svg>
    ),
  },
  {
    label: 'RSS Feed',
    desc: 'Works with any CMS, news reader, or aggregator.',
    href: '#rss-feed',
    icon: (
      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 19.5v-.75a7.5 7.5 0 0 0-7.5-7.5H4.5m0-6.75h.75c7.87 0 14.25 6.38 14.25 14.25v.75M6 18.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
      </svg>
    ),
  },
  {
    label: 'Embed',
    desc: 'One line of code on any webpage.',
    href: '#embed-widget',
    icon: (
      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9.75 16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
      </svg>
    ),
  },
];

const SECTION_IDS = NAV_ITEMS.map((n) => n.id);

export default function Docs() {
  const active = useActiveSection(SECTION_IDS);

  return (
    <div className="bg-[#050505] min-h-screen pb-40">

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/[0.05]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-[#CCFF00]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute top-0 right-0 w-[400px] h-[300px] bg-[#CCFF00]/[0.02] rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-36 pb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#CCFF00]/20 bg-[#CCFF00]/[0.05] px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#CCFF00] animate-pulse" />
            <span className="text-[9px] font-black uppercase tracking-[0.4em] text-[#CCFF00]/80">Developer Docs</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-black tracking-tighter text-white mb-5 leading-none">
            API Reference
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed max-w-xl mb-10">
            Integrate live, translated football news directly into your platform.
            Your API key and delivery settings are configured by the Pitch Feed team when your account is created.
          </p>
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 w-fit">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">Base URL</span>
            <span className="w-px h-3 bg-white/10" />
            <code className="font-mono text-sm text-[#CCFF00]">https://thepitchfeed.com</code>
          </div>
        </div>

        {/* Quick-start cards */}
        <div className="relative max-w-7xl mx-auto px-6 pb-14">
          <p className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 mb-5">
            Choose your integration
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {QUICK_START.map((q) => (
              <a
                key={q.label}
                href={q.href}
                className="group flex items-start gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-5 py-5 transition-all duration-200 hover:border-[#CCFF00]/25 hover:bg-[#CCFF00]/[0.03] hover:shadow-[0_0_32px_rgba(204,255,0,0.06)]"
              >
                <span className="mt-0.5 text-slate-600 group-hover:text-[#CCFF00] transition-colors">
                  {q.icon}
                </span>
                <div>
                  <p className="font-black text-white text-sm mb-1 group-hover:text-[#CCFF00] transition-colors">{q.label}</p>
                  <p className="text-[12px] text-slate-500 leading-snug">{q.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-14">
        <div className="flex gap-16 items-start">

          {/* Sidebar */}
          <aside className="hidden lg:block w-52 shrink-0 sticky top-28 self-start">
            <p className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 mb-4">
              On this page
            </p>
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-white/[0.06]" />
              <nav className="pl-4 space-y-0.5">
                {NAV_ITEMS.map((item) => {
                  const isActive = active === item.id;
                  return (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={`relative flex items-center gap-2.5 rounded-r-lg py-2 pr-2 text-[12px] font-medium transition-all duration-150 ${
                        isActive
                          ? 'text-[#CCFF00] font-bold'
                          : 'text-slate-500 hover:text-slate-200'
                      }`}
                    >
                      {isActive && (
                        <span className="absolute -left-4 top-0 bottom-0 w-px bg-[#CCFF00]" />
                      )}
                      <span className="font-mono text-[9px] text-slate-700 shrink-0 w-6">{item.num}</span>
                      {item.label}
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">

            <Section id="getting-started">
              <SectionTitle num="01">Getting Started</SectionTitle>
              <Para>
                The Pitch Feed API gives you access to a continuous stream of professional football news,
                automatically translated into your language of choice. Every account comes with a unique
                API key that identifies your organisation and controls how your feed is delivered.
              </Para>
              <Para>
                Your API key is provided by the Pitch Feed team when your subscription is activated.
                If you have not received your key or need a replacement, contact your account manager.
              </Para>
            </Section>

            <Section id="authentication">
              <SectionTitle num="02">Authentication</SectionTitle>
              <Para>
                Pass your API key in the <Mono>X-Api-Key</Mono> request header on every call.
              </Para>
              <CodeBlock lang="HTTP">
{`GET /v1/feed HTTP/1.1
Host: thepitchfeed.com
X-Api-Key: YOUR_API_KEY`}
              </CodeBlock>
              <Para>
                For RSS and embed URLs where headers are not practical, pass the key
                as an <Mono>api_key</Mono> query parameter instead:
              </Para>
              <CodeBlock lang="URL">
{`https://thepitchfeed.com/v1/feed.rss?api_key=YOUR_API_KEY`}
              </CodeBlock>
              <Callout type="warning">
                Keep your API key private. Do not expose it in public client-side code or commit it to version control.
                Contact your account manager immediately if you believe a key has been compromised.
              </Callout>
            </Section>

            <Section id="json-feed">
              <SectionTitle num="03">JSON Feed</SectionTitle>
              <Para>
                The primary endpoint for developers. Returns the latest articles for your account,
                translated into your delivery language and ready to render.
              </Para>
              <EndpointBar method="GET" path="/v1/feed" />

              <SubHeading>Query Parameters</SubHeading>
              <Table
                headers={['Parameter', 'Type', 'Default', 'Description']}
                rows={[
                  [<Mono>lang</Mono>, 'string', 'Account default', 'Return articles in this language. EN returns the original English copy.'],
                ]}
              />

              <SubHeading>Example Request</SubHeading>
              <CodeBlock lang="cURL">
{`curl https://thepitchfeed.com/v1/feed \\
  -H "X-Api-Key: YOUR_API_KEY"`}
              </CodeBlock>

              <SubHeading>Example Response</SubHeading>
              <CodeBlock lang="JSON">
{`{
  "customer": {
    "id": "cust_abc123",
    "name": "Acme Sports Media",
    "target_lang": "DA"
  },
  "items": [
    {
      "id": "art_340858",
      "headline": "PSG retter blikket mod AFC Bournemouths Eli Junior Kroupi",
      "summary": "Paris Saint-Germain ønsker at styrke truppen...",
      "body_html": "<p>Efter at have forsvaret UEFA Champions League-titlen...</p>",
      "category": "transfers",
      "published_at": "2026-05-31T21:30:30Z"
    }
  ]
}`}
              </CodeBlock>
            </Section>

            <Section id="single-article">
              <SectionTitle num="04">Single Article</SectionTitle>
              <Para>
                Fetch a single article by its ID. Returns the full body including any extended
                long-form content not included in the feed list.
              </Para>
              <EndpointBar method="GET" path="/v1/articles/:id" />

              <SubHeading>Query Parameters</SubHeading>
              <Table
                headers={['Parameter', 'Type', 'Default', 'Description']}
                rows={[
                  [<Mono>lang</Mono>, 'string', 'Account default', 'Language for the article body. Falls back to your account delivery language if not specified.'],
                ]}
              />

              <SubHeading>Example Request</SubHeading>
              <CodeBlock lang="cURL">
{`curl https://thepitchfeed.com/v1/articles/art_340858 \\
  -H "X-Api-Key: YOUR_API_KEY"`}
              </CodeBlock>

              <SubHeading>Example Response</SubHeading>
              <CodeBlock lang="JSON">
{`{
  "customer": {
    "id": "cust_abc123",
    "name": "Acme Sports Media",
    "target_lang": "DA"
  },
  "item": {
    "id": "art_340858",
    "headline": "PSG retter blikket mod AFC Bournemouths Eli Junior Kroupi",
    "summary": "Paris Saint-Germain ønsker at styrke truppen...",
    "body_html": "<p>Full article body...</p>",
    "published_at": "2026-05-31T21:30:30Z"
  },
  "detail_body_html": "<p>Extended full-length article body...</p>"
}`}
              </CodeBlock>
              <Para>
                The <Mono>detail_body_html</Mono> field contains the full article body when extended
                content is available. It may be <Mono>null</Mono> if no extended content exists.
              </Para>
            </Section>

            <Section id="rss-feed">
              <SectionTitle num="05">RSS Feed</SectionTitle>
              <Para>
                A standard RSS 2.0 feed compatible with any RSS reader, content management system,
                or news aggregator. No special headers required — paste the URL directly into your tool.
              </Para>
              <EndpointBar method="GET" path="/v1/feed.rss" />
              <CodeBlock lang="URL">
{`https://thepitchfeed.com/v1/feed.rss?api_key=YOUR_API_KEY`}
              </CodeBlock>
              <Para>
                Articles are delivered in your account's configured delivery language.
              </Para>
            </Section>

            <Section id="embed-widget">
              <SectionTitle num="06">Embed Widget</SectionTitle>
              <Para>
                Add a live football news feed to any webpage without writing backend code.
                Two options are available depending on your preference.
              </Para>

              <div className="mt-6 rounded-2xl border border-white/[0.06] overflow-hidden">
                <div className="px-6 pt-6 pb-4 border-b border-white/[0.05]">
                  <p className="text-sm font-black text-white mb-1">Option 1 — JavaScript Snippet</p>
                  <p className="text-[13px] text-slate-500">Paste one line where you want the feed to appear.</p>
                </div>
                <div className="px-6 py-4">
                  <CodeBlock lang="HTML">
{`<script src="https://thepitchfeed.com/v1/embed.js?api_key=YOUR_API_KEY"></script>`}
                  </CodeBlock>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-white/[0.06] overflow-hidden">
                <div className="px-6 pt-6 pb-4 border-b border-white/[0.05]">
                  <p className="text-sm font-black text-white mb-1">Option 2 — iframe</p>
                  <p className="text-[13px] text-slate-500">Full style isolation, works on any platform.</p>
                </div>
                <div className="px-6 py-4">
                  <CodeBlock lang="HTML">
{`<iframe
  src="https://thepitchfeed.com/v1/embed?api_key=YOUR_API_KEY"
  width="100%"
  height="600"
  frameborder="0"
  title="The Pitch Feed"
></iframe>`}
                  </CodeBlock>
                </div>
              </div>
            </Section>

            <Section id="languages">
              <SectionTitle num="07">Languages</SectionTitle>
              <Para>
                Your feed is delivered in the language configured for your account.
                We support a wide range of languages — if you need your feed in a specific language,
                simply let your account manager know and we will set it up for you.
              </Para>
              <Para>
                You can also request a different language on a per-request basis using
                the <Mono>lang</Mono> query parameter on any endpoint.
              </Para>
              <Callout type="info">
                We can configure delivery in any language. Contact your account manager and we will get it set up.
              </Callout>
            </Section>

            {/* Contact CTA */}
            <div className="relative mt-8 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#CCFF00]/[0.06] to-transparent pointer-events-none" />
              <div className="relative border border-[#CCFF00]/[0.12] rounded-2xl px-8 py-10">
                <p className="text-[9px] font-black uppercase tracking-[0.4em] text-[#CCFF00]/60 mb-3">
                  Need help?
                </p>
                <p className="text-2xl font-black text-white mb-3 tracking-tight">
                  We're here to help you integrate.
                </p>
                <p className="text-slate-400 text-sm leading-relaxed mb-7 max-w-md">
                  Questions about your API key, delivery language, or anything else?
                  Reach out and we'll get back to you quickly.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#CCFF00] px-6 py-3 text-[11px] font-black uppercase tracking-widest text-black transition-all hover:bg-white hover:shadow-[0_0_32px_rgba(204,255,0,0.3)]"
                >
                  Contact us
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
}
